<template>
    <b-row>
        <b-col cols="12">
            <div class="text-center text-primary my-2" v-if="!component">
                <b-skeleton width="100%" height="250px" class="align-middle" animation="throb"/>
            </div>

            <div v-else>
                <PowerApp 
                    :id="getComponentID()"
                    :nodes="component.settings.templates" 
                    v-if="isCustomTemplate" 
                    :entrypoint="templateEntrypoint" 
                    :modelUuid="modelId" 
                    :component="component"
                    :schema="selectedSchema"
                    
                    noRouter

                    v-on="$listeners"

                    v-bind="$attrs"
                />

                <DefaultEdit 
                    :id="getComponentID()" 
                    :modelUuid="modelId" 
                    :component="component" 
                    @modelChanged="modelChanged" 
                    @saved="saved" 
                    v-bind="$attrs" 
                    v-else
                >
                    <template #controls="props">
                        <slot name="controls" v-bind="props"></slot>
                    </template>
                </DefaultEdit>
            </div>

            <protokol-floating-settings v-if="component && (($can('settings ' + component.general.getRef()) && $isPlatformEnv('dev') && $screen.md) || (currentModel && currentModel.uuid))">
                <template v-slot="{toggleSettings}">
                    <b-button variant="dark" @click="toggleSettings()" :to="settingsRoute" v-if="$can('settings ' + component.general.getRef()) && $isPlatformEnv('dev') && $screen.md && !isCustomTemplate">
                        {{ $t('settings.title') }}
                    </b-button>
                    <b-button variant="info" @click="openRevisions(); toggleSettings()" v-b-tooltip.hover :title="$t('viewRevisions')" v-if="currentModel && currentModel.uuid">
                        <b-icon icon="clock-history" class="mr-1"/>
                        {{ $t('revisions') }}
                    </b-button>
                </template>
            </protokol-floating-settings>

            <RevisionsManager 
                ref="revisionsManager"
                :component-uuid="getComponentID()"
                :model-uuid="modelId"
                :current-version="currentModel ? currentModel.__version__ : null"
                :current-model="currentModel"
                :fields="component ? component.settings.fields : []"
                :can-restore="component && $can('edit ' + component.general.getRef())"
                @restore="handleRestoreRevision"
                v-if="component && currentModel && currentModel.uuid"
            />
        </b-col>
    </b-row>
</template>

<script>
import DefaultEdit from './DefaultEdit.vue'
import RevisionsManager from './Revisions/RevisionsManager.vue'

export default {
    name: 'Edit',
    components: {
        DefaultEdit,
        PowerApp: () => import('./ComponentView'),
        RevisionsManager
    },
    props: {
        id: {
            type: String,
            default: null
        },
        modelUuid: {
            type: String,
            default: null
        },
        entrypoint: {
            type: String,
        }
    },
    data: () => {
        return {
            component: null,
            currentModel: null,
            selectedSchema: ''
        }
    },
    computed: {
        isCustomTemplate() {
            return this.templateEntrypoint != ''
        },

        templateEntrypoint() {
            if (this.entrypoint == 'default') return ''
            if (this.entrypoint) return this.entrypoint

            return this.component.settings.appearance.edit_entrypoint
        },

        modelId() {
            return this.modelUuid || this.$route.params.uuid
        },

        settingsRoute() {
            if (!this.$route) return '/settings#fields'
            
            const componentRef = this.getComponentID()
            return `/admin/component/${componentRef}/settings#fields`
        }
    },
    methods: {
        getBaseRef() {
            return this.id || (this.$route ? this.$route.meta.ref : null)
        },
        
        getComponentID() {
            if (this.id) {
                return this.id
            }
            
            let ref = this.$route ? this.$route.meta.ref : null
            
            // Include schema in the ref if schema is selected
            if (ref && this.component) {
                // Use base ref for storage key, not the computed ref with schema
                const baseRef = this.getBaseRef()
                const storageKey = `component_${baseRef}_schema`
                const selectedSchema = localStorage.getItem(storageKey)
                
                if (selectedSchema) {
                    ref = `${ref}.${selectedSchema}`
                }
            }
            
            return ref
        },

        modelChanged(model) {
            this.currentModel = model
            this.$emit('modelChanged', model)
        },

        saved(model) {
            this.currentModel = model
            this.$emit('saved', model)
        },

        openRevisions() {
            if (this.$refs.revisionsManager) {
                this.$refs.revisionsManager.open()
            }
        },

        handleRestoreRevision(revision) {
            const popup = this.$loadingPopup()

            this.$component(this.getComponentID()).store().restoreRevision(this.modelId, revision.__version__).then((resp) => {
                this.$bvToast.toast(this.$t('revisionRestored'), {
                    variant: 'success',
                    title: this.$t('success')
                })

                this.currentModel = resp.data
                this.$store.commit("SET_COMPONENTS_MODEL", {uuid: this.getComponentID(), results: resp.data})
                this.$emit('saved', resp.data)
            }).catch((error) => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    if (error.response.status == 417) {
                        this.$bvToast.toast(this.$t('validationError'), {
                            variant: 'danger'
                        })
                        return
                    }

                    if (error.response.status == 409) {
                        this.$bvToast.toast(this.$t('concurrentModelUpdate'), {
                            variant: 'warning'
                        })
                        return
                    }

                    this.$bvToast.toast(error.response.data.message, {
                        variant: 'danger'
                    })
                }

                this.$debugLog(error)
            }).finally(() => {
                popup.hide()
            })
        }
    },
    created() {
        this.$store.dispatch("fetchComponentsSettings", this.getBaseRef()).then(data => {
            this.component = data
            
            // Initialize selected schema from localStorage
            const baseRef = this.getBaseRef()
            const storageKey = `component_${baseRef}_schema`
            const storedSchema = localStorage.getItem(storageKey)
            this.selectedSchema = storedSchema !== null ? storedSchema : ''

            if (this.$route && this.$route.meta) {
                this.$route.meta.label = this.$translateLabel(this.component.general.label)
            }
        }).catch(err => {

            console.error(err)
            this.$bvToast.toast(this.$t('unexpectedError'), {
                variant: 'danger'
            })
        }) 
    
    },

    updated() {
        console.log("[Render] Edit rendered")
    }
}
</script>
